import type { ReactNode } from 'react';

export type HousekeepingStatus = 'dirty' | 'clean' | 'inspected';
export type ServiceStatus = 'in_service' | 'out_of_order' | 'out_of_service';

/** Room status chip — colour plus a letter/icon cue so colour-blind users can read it (11 §6.2). */
export function RoomStatusChip({
  housekeeping,
  service,
  occupied,
  labels,
}: {
  housekeeping: HousekeepingStatus;
  service: ServiceStatus;
  occupied: boolean;
  labels: {
    dirty: string;
    clean: string;
    inspected: string;
    ooo: string;
    oos: string;
    occupied: string;
    vacant: string;
  };
}) {
  if (service === 'out_of_order')
    return (
      <Chip tone="ooo" cue="✕">
        {labels.ooo}
      </Chip>
    );
  if (service === 'out_of_service')
    return (
      <Chip tone="ooo" cue="–">
        {labels.oos}
      </Chip>
    );
  const hk =
    housekeeping === 'inspected'
      ? labels.inspected
      : housekeeping === 'clean'
        ? labels.clean
        : labels.dirty;
  const cue = housekeeping === 'inspected' ? '✓✓' : housekeeping === 'clean' ? '✓' : '!';
  return (
    <span className="inline-flex items-center gap-1">
      <Chip tone={occupied ? 'occupied' : 'vacant'} cue={occupied ? '●' : '○'}>
        {occupied ? labels.occupied : labels.vacant}
      </Chip>
      <Chip tone={housekeeping} cue={cue}>
        {hk}
      </Chip>
    </span>
  );
}

const TONES: Record<string, string> = {
  dirty: 'bg-room-dirty/15 text-room-dirty border-room-dirty/40',
  clean: 'bg-room-clean/15 text-room-clean border-room-clean/40',
  inspected: 'bg-room-inspected/15 text-room-inspected border-room-inspected/40',
  ooo: 'bg-room-ooo/15 text-room-ooo border-room-ooo/40',
  occupied: 'bg-room-occupied/15 text-room-occupied border-room-occupied/40',
  vacant: 'bg-surface-muted text-text-muted border-border',
  neutral: 'bg-surface-muted text-text border-border',
  success: 'bg-success/15 text-success border-success/40',
  warning: 'bg-warning/15 text-warning border-warning/40',
  danger: 'bg-danger/15 text-danger border-danger/40',
  info: 'bg-info/15 text-info border-info/40',
};

export function Chip({
  tone = 'neutral',
  cue,
  children,
}: {
  tone?: string | undefined;
  cue?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1 rounded-sm border px-1.5 py-0.5 text-xs font-semibold whitespace-nowrap ${TONES[tone] ?? TONES['neutral']}`}
    >
      {cue ? (
        <span aria-hidden="true" className="num">
          {cue}
        </span>
      ) : null}
      {/* a long value (disk sizes, paths) ellipsises inside a narrow card instead of pushing past it */}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

export const RESERVATION_TONE: Record<string, string> = {
  tentative: 'warning',
  confirmed: 'info',
  checked_in: 'occupied',
  checked_out: 'neutral',
  cancelled: 'danger',
  no_show: 'danger',
  moved: 'neutral',
};
